import React, { useId } from "react";
import { Upload, Paperclip } from "lucide-react";
import CommonTooltip from "./CommonTooltip";

/**
 * Platform-standard file-input visual (icon chip + blue "Choose File" pill + filename text +
 * paperclip), matching src/components/support/CreateInstallment.jsx's "Attach Proof" field.
 * Purely presentational -- the real `<input type="file">` is layered underneath, fully
 * transparent, so disabled/multiple/accept/onChange behave exactly like a native file input.
 */
const SIZE_STYLES = {
  sm: {
    wrapper: "px-2.5 py-2",
    icon: "h-7 w-7",
    iconSize: 13,
    pill: "px-2.5 py-1 text-[11px]",
    fileName: "text-[11px]",
    paperclip: 13,
  },
  md: {
    wrapper: "px-3 py-2.5",
    icon: "h-8 w-8",
    iconSize: 15,
    pill: "px-3 py-1.5 text-xs sm:text-sm",
    fileName: "text-xs sm:text-sm",
    paperclip: 16,
  },
};

const FileChooserPreview = ({
  id,
  disabled = false,
  multiple = false,
  accept,
  onChange,
  fileName,
  chooseFileText = "Choose File",
  theme,
  size = "md",
  className = "",
}) => {
  const s = SIZE_STYLES[size] || SIZE_STYLES.md;
  const generatedId = useId();
  const inputId = id || generatedId;

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg border transition-colors focus-within:border-blue-500 focus-within:outline-none ${s.wrapper} ${
        theme === "dark"
          ? "border-gray-600 bg-[#0f172a]"
          : theme === "high-contrast"
            ? "border-black bg-white"
            : "border-gray-300 bg-white"
      } ${className}`}
    >
      {/* The input ignores the mouse and a label laid over the control opens the picker, so the
          browser has no input under the pointer to attach its own "No file chosen" bubble to. */}
      <input
        id={inputId}
        type="file"
        disabled={disabled}
        multiple={multiple}
        accept={accept}
        onChange={onChange}
        className="pointer-events-none absolute inset-0 z-20 h-full w-full opacity-0"
      />
      <label
        htmlFor={inputId}
        aria-hidden="true"
        className={`absolute inset-0 z-30 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      />
      <div className="relative flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`inline-flex shrink-0 items-center justify-center rounded-lg bg-[#236DB4]/10 text-[#236DB4] ${s.icon}`}
          >
            <Upload size={s.iconSize} />
          </span>
          <span
            className={`inline-flex shrink-0 rounded-lg bg-[#236DB4] font-semibold text-white ${s.pill}`}
          >
            {chooseFileText}
          </span>
          {/* Only the file name carries the tooltip, so the bubble opens above the name itself
              instead of at the middle of a wide field. It sits above the click overlay and is a
              label for the input, so clicking it still opens the picker. */}
          <CommonTooltip
            text={fileName ? String(fileName) : ""}
            position="top"
            disableDefaultDescription
            className="relative z-40 min-w-0"
            tooltipClassName="w-max max-w-[min(20rem,calc(100vw-1.5rem))] break-all"
            fitContent
            clampToViewport
            hoverOnly
          >
            <label
              htmlFor={inputId}
              className={`min-w-0 truncate ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${s.fileName} ${theme === "dark" ? "text-gray-200" : "text-gray-600"}`}
            >
              {fileName}
            </label>
          </CommonTooltip>
        </div>
        <div className={`shrink-0 text-gray-400 ${theme === "dark" ? "text-gray-300" : ""}`}>
          <Paperclip size={s.paperclip} />
        </div>
      </div>
    </div>
  );
};

export default FileChooserPreview;
